import { readCookies } from './cookies'
import { logger } from './logger'

/**
 * Marketing-automation identity cookies (CDP-326).
 *
 * HubSpot, Marketo, Eloqua and Pardot each keep a first-party cookie on the customer's site that
 * identifies the visitor to that tool. When the visitor is a known prospect there, the token is the
 * key to a contact record — without asking for an email. We attach the tokens to every jitsu event
 * as `context.mapIds` (flattened to `context_map_ids_*` in the warehouse); resolving them to
 * contacts through the MAP integrations is pipeline work.
 *
 * - HubSpot: `hubspotutk` (the usertoken), with the utk segment of `__hstc` as fallback.
 * - Marketo: `_mkto_trk` (`id:<munchkin id>&token:<token>`), kept whole — associating a lead needs
 *   both.
 * - Eloqua: the `GUID=` segment of the `ELOQUA` cookie.
 * - Pardot: `visitor_id<account id>` (the numeric visitor id; the `-hash` sibling is left alone).
 *
 * Cookies are read at send time, so a tag that sets its cookie after our init is picked up by the
 * next event. Nothing is written, and nothing is sent while tracking is paused.
 */

type MapIds = {
  hubspotUtk?: string
  marketoToken?: string
  eloquaGuid?: string
  pardotVisitorId?: string
  pardotAccountId?: string
}

const HUBSPOT_UTK = /^[a-f0-9]{32}$/i
const PARDOT_VISITOR_COOKIE = /^visitor_id(\d+)$/

// __hstc holds `<domain hash>.<utk>.<first visit>.<previous visit>.<current visit>.<visit count>`.
const parseHstcUtk = (value: string | undefined): string | undefined => {
  if (!value) return undefined
  const utk = value.split('.')[1]
  return utk && HUBSPOT_UTK.test(utk) ? utk : undefined
}

// ELOQUA holds `GUID=<guid>&FPCVISITED=1`.
const parseEloquaGuid = (value: string | undefined): string | undefined => {
  if (!value) return undefined
  return new URLSearchParams(value).get('GUID') || undefined
}

export const readMapIds = (): MapIds => {
  const cookies = readCookies()
  const mapIds: MapIds = {}

  const hubspotUtk = cookies['hubspotutk'] ?? parseHstcUtk(cookies['__hstc'])
  if (hubspotUtk) mapIds.hubspotUtk = hubspotUtk

  const marketoToken = cookies['_mkto_trk']
  if (marketoToken) mapIds.marketoToken = marketoToken

  const eloquaGuid = parseEloquaGuid(cookies['ELOQUA'])
  if (eloquaGuid) mapIds.eloquaGuid = eloquaGuid

  for (const [name, value] of Object.entries(cookies)) {
    const match = name.match(PARDOT_VISITOR_COOKIE)
    const accountId = match?.[1]
    if (accountId && value) {
      mapIds.pardotVisitorId = value
      mapIds.pardotAccountId = accountId
      break
    }
  }

  return mapIds
}

/**
 * Re-reads the MAP cookies and attaches `context.mapIds` on the jitsu context. Called before every
 * outgoing event, like refreshClientIds. Never throws — the tracker runs on customer sites.
 */
export const refreshMapIds = (): void => {
  if (!window.jitsu) return
  try {
    const mapIds = readMapIds()
    if (Object.keys(mapIds).length === 0) return
    window.jitsu.setContextProperty('mapIds', mapIds)
  } catch (error) {
    logger.warn('Failed to refresh mapIds', error)
  }
}
